import { useCallback, useEffect, useMemo, useState } from "react";
import { useLogout, useNotify, usePermissions } from "react-admin";
import { Navigate } from "react-router-dom";
import AppBar from "@mui/material/AppBar";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import CssBaseline from "@mui/material/CssBaseline";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";

import { getApiUrl } from "@/constants";
import type { PermissionRecord } from "@/types/permissions";
import { shouldRedirectToDistributorPortal } from "@/utils/permission-utils";
import httpClient from "@/dataprovider/http-client";

import PortalRosterList from "./PortalRosterList";
import {
  buildPortalKitchenPickupSummary,
  filterPortalRoster,
  type PortalRosterRow,
} from "./fmb-distributor-portal-roster";

type PortalMeta = { name: string; timezone: string | null };

type ThaliTypeRow = { id: string; code: string; name: string; sortOrder: number };

function rowsFromResponse(json: unknown): unknown[] {
  if (
    json &&
    typeof json === "object" &&
    "rows" in json &&
    Array.isArray((json as { rows: unknown }).rows)
  ) {
    return (json as { rows: unknown[] }).rows;
  }
  return [];
}

function todayYmdUtc(): string {
  return new Date().toISOString().slice(0, 10);
}

export default function FmbDistributorPortalPage() {
  const { permissions, isLoading: permissionsLoading } = usePermissions<PermissionRecord>();
  const isPortalUser = shouldRedirectToDistributorPortal(permissions);

  const notify = useNotify();
  const logout = useLogout();
  const [date, setDate] = useState(todayYmdUtc);
  const [meta, setMeta] = useState<PortalMeta | null>(null);
  const [roster, setRoster] = useState<PortalRosterRow[]>([]);
  const [thaliTypes, setThaliTypes] = useState<ThaliTypeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasDailyMenu, setHasDailyMenu] = useState(true);
  const [rosterSearch, setRosterSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [metaRes, runRes, typesRes] = await Promise.all([
        httpClient(`${getApiUrl()}/fmbDistributorPortal/meta`, { method: "GET" }),
        httpClient(
          `${getApiUrl()}/fmbDistributorPortal/daily-run?${new URLSearchParams({ date }).toString()}`,
          { method: "GET" }
        ),
        httpClient(`${getApiUrl()}/fmbDistributorPortal/thali-types`, { method: "GET" }),
      ]);
      setMeta(metaRes.json as PortalMeta);
      const runJson = runRes.json as {
        hasDailyMenu?: boolean;
      };
      setHasDailyMenu(runJson.hasDailyMenu !== false);
      setRoster(rowsFromResponse(runRes.json) as PortalRosterRow[]);
      setThaliTypes(rowsFromResponse(typesRes.json) as ThaliTypeRow[]);
    } catch (e: unknown) {
      notify(e instanceof Error ? e.message : "Failed to load roster", { type: "warning" });
    } finally {
      setLoading(false);
    }
  }, [date, notify]);

  useEffect(() => {
    if (permissionsLoading || !isPortalUser) {
      return;
    }
    void load();
  }, [load, permissionsLoading, isPortalUser]);

  const displayRoster = useMemo(
    () => filterPortalRoster(roster, rosterSearch),
    [roster, rosterSearch]
  );

  const kitchenPickup = useMemo(
    () => buildPortalKitchenPickupSummary({ thaliTypes, roster }),
    [roster, thaliTypes]
  );

  if (permissionsLoading) {
    return (
      <>
        <CssBaseline />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "40vh",
          }}
        >
          <CircularProgress />
        </Box>
      </>
    );
  }

  if (!isPortalUser) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      <CssBaseline />
      <AppBar position="sticky" elevation={1} sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Toolbar sx={{ gap: 2, flexWrap: "wrap" }}>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Thali distribution
          </Typography>
          <Button color="inherit" onClick={() => void logout()}>
            Log out
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 2, maxWidth: 1200, mx: "auto" }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
            <TextField
              label="Service date"
              type="date"
              size="small"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 200 }}
            />
            {meta && (
              <Typography variant="body1" color="text.secondary">
                {meta.name}
              </Typography>
            )}
          </Stack>

          {!hasDailyMenu && <Alert severity="info">No menu for this date — no deliveries.</Alert>}

          {!loading && hasDailyMenu && (thaliTypes.length > 0 || roster.length > 0) && (
            <Paper
              variant="outlined"
              sx={(theme) => ({
                p: 1.5,
                bgcolor: alpha(theme.palette.primary.main, 0.06),
                borderColor: "primary.light",
              })}
            >
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Pick up from kitchen
              </Typography>
              {thaliTypes.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No active thali types are configured for this jamaat.
                </Typography>
              ) : (
                <Stack spacing={0.75}>
                  {kitchenPickup.map((row) => {
                    const isTotal = row.id === "__total";
                    return (
                      <Stack
                        key={row.id}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={2}
                        sx={
                          isTotal
                            ? {
                                pt: 0.75,
                                borderTop: 1,
                                borderColor: "divider",
                              }
                            : undefined
                        }
                      >
                        <Typography
                          variant="body2"
                          color={isTotal ? "text.primary" : "text.secondary"}
                          sx={
                            isTotal
                              ? (theme) => ({ fontWeight: theme.typography.fontWeightMedium })
                              : undefined
                          }
                        >
                          {row.label}
                        </Typography>
                        <Typography
                          variant="body1"
                          color={isTotal ? "primary" : "text.primary"}
                          sx={(theme) => ({ fontWeight: theme.typography.fontWeightMedium })}
                        >
                          {row.count}
                        </Typography>
                      </Stack>
                    );
                  })}
                </Stack>
              )}
            </Paper>
          )}

          <Box>
            <Button variant="text" disabled={loading} onClick={() => void load()}>
              Refresh
            </Button>
          </Box>

          {!loading && hasDailyMenu && roster.length > 0 ? (
            <TextField
              label="Filter roster"
              placeholder="Thali, type, address…"
              size="small"
              value={rosterSearch}
              onChange={(e) => setRosterSearch(e.target.value)}
              sx={{ maxWidth: 400 }}
              fullWidth
            />
          ) : null}

          <PortalRosterList
            loading={loading}
            hasDailyMenu={hasDailyMenu}
            rosterCount={roster.length}
            rows={displayRoster}
          />
        </Stack>
      </Box>
    </>
  );
}
